import i18n from '@/locales/i18n';
import type { FormList } from '@/types/formBuilder';
import type { MTableColumn } from '@/types/mTable';
import type { Path } from 'vue-i18n';
import { useUserStore } from '@/stores/modules/user';
import { downLoadFile } from '@/utils/download';
import { sessionS } from '@/utils/cache';
import router from '@/router';
// import userAPi from '@/api/user';

const { t }: any = i18n.global;
export const setTreeData = (data: Record<string, any>[], id = -1): Record<string, any>[] => {
    const top = data.filter((item) => item.parentId === id);
    return top.map((item) => {
        const children = setTreeData(data, item.id);
        return {
            ...item,
            children: children.length ? children : null
        };
    });
};

export const T = (key: Path | number, options?: any): string => {
    return t(key, options);
};

export const transformColumnsToForm = (columns: MTableColumn[]) => {
    let columnsFormList: FormList[] = [];
    columns.forEach((item) => {
        if (item.form) {
            const formObj: FormList & { index: number } = {
                index: item.form.index || 0,
                label: item.form.label || item.label,
                key: item.form.key || item.prop,
                tagName: item.form.tagName,
                required: 'required' in item.form ? item.form.required : true,
                props: item.form.props || {},
                colSpan: item.form.colSpan || 12
            };
            if ('defaultValue' in item.form) {
                formObj.defaultValue = item.form.defaultValue;
            }
            if ('rules' in item.form) {
                formObj.rules = item.form.rules;
            }
            if ('on' in item.form) {
                formObj.on = item.form.on;
            }
            if ('hidden' in item.form) {
                formObj.hidden = item.form.hidden;
            }
            if (item.form.tagName === 'el-select') {
                formObj.children = [
                    {
                        tagName: 'el-option',
                        props: item.form.optionsProps || {
                            label: 'name',
                            value: 'id'
                        },
                        options: item.form.options || [],
                        innerValue: item.form.innerValue!
                    }
                ];
            }
            if (item.form.tagName === 'el-radio-group') {
                formObj.children = [
                    {
                        tagName: 'el-radio',
                        innerValue: item.form.innerValue! || 'name',
                        options: item.form.options || [],
                        props: item.form.optionsProps || {
                            label: 'id'
                        }
                    }
                ];
            }
            if ('slots' in item.form) {
                formObj.slots = item.form.slots;
            }
            if ('customSlots' in item.form) {
                formObj.children = [{ customSlots: item.form.customSlots }];
            }
            columnsFormList.push(formObj);
        }
    });
    columnsFormList = columnsFormList.sort((a: any, b: any) => {
        if (a.index < b.index) return -1;
        return 1;
    });
    return columnsFormList;
};

export const resetReactive = (aimObj: Record<string, any>, assignObj: Record<string, any>) => {
    Object.keys(aimObj).map((key) => {
        delete aimObj[key];
    });
    Object.assign(aimObj, assignObj);
};

export const resetArr = (aimArr: any[], assiArr?: any[]) => {
    aimArr.splice(0, aimArr.length);
    assiArr &&
        assiArr.forEach((item: any) => {
            aimArr.push(item);
        });
    return aimArr;
};
export function getUuid(): string {
    const s: any[] = [];
    const hexDigits = '0123456789abcdef';
    for (let i = 0; i < 36; i++) {
        s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
    s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23] = '-';

    const uuid = s.join('');
    return uuid;
}

// 获取图片转base64
export const getBase64 = (file: any) => {
    return new Promise(function (resolve, reject) {
        const reader = new FileReader();
        let imgResult: any = '';
        reader.readAsDataURL(file);
        reader.onload = function () {
            imgResult = reader.result;
        };
        reader.onerror = function (error) {
            reject(error);
        };
        reader.onloadend = function () {
            resolve(imgResult);
        };
    });
};
// 验证版本号
export const isValidVersion = (version: any) => {
    // 版本号必须由三部分组成，每部分为数字，可以以 'v' 或 'V' 开头
    const versionRegex = /^(v|V)?\d+\.\d+\.\d+$/;

    return versionRegex.test(version);
};
export const debounce = <F extends (...args: any[]) => void>(func: F, delay: number): ((...args: Parameters<F>) => void) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<F>) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            func(...args);
        }, delay);
    };
};
// 下载文件
export const downLoad = async (url: string, data: any) => {
    downLoadFile(url, data);
};

// 判断用户的权限
export const hasPermission = (role: number[]) => {
    const store = useUserStore();
    const roleMap = store.userInfo?.roleMap;
    const level = Object.values(roleMap!);
    // 如果是DSA的用户,那么就是有所有的权限
    if (level.includes(1)) {
        return true;
    }
    if (role.length === 0) {
        return false;
    } else {
        return level.some((item) => role.includes(item));
    }
};
export const isSSO = () => {
    return sessionS.get('loginMode') == 'sso';
};
// 获取用户权限的等级
export const getPermissionLevel = () => {
    const store = useUserStore();
    const roleMap = store.userInfo?.roleMap;
    const level = Object.values(roleMap!);
    return Math.min(...level);
};

// export const letterCasePermutation = (arr: string[] = []) => {
//     let res: string[] = [];
//     const fn: any = (str: string) => {
//         if (str.length === 0) return [''];
//         const a = str[0],
//             b = [a.toLowerCase(), a.toUpperCase()];
//         b[0] === b[1] && b.pop();
//         if (str.length === 1) return b;
//         return fn(str.slice(1)).reduce((r: string[], c: string) => [...r, ...b.map((m) => m + c)], []);
//     };
//     arr.forEach((element: string) => {
//         res = res.concat(fn(element));
//     });
//     return res;
// };

export const letterCasePermutation = (arr: string[] = []) => {
    const res: string[] = [];
    arr.forEach((element: string) => {
        res.push(element.toLowerCase());
        res.push(element.toUpperCase());
    });
    return res;
};
// 获取当前的时间(YYMMDDHHMMSS格式)
export const formatDateToYYMMDDHHMMSS = () => {
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
};

// 从UTC时间字符串中截取精度到秒的时间字符串
export const fixUTCTimePrecision = (utcTime: string) => {
    // 检查传入的字符串是否是有效的日期
    if (isNaN(Date.parse(utcTime))) {
        // return 'Invalid UTC time format';
        return '';
    }

    // 将UTC时间转换为本地时间
    const date = new Date(utcTime);

    // 格式化日期和时间
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份是从0开始的
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // 返回格式化后的字符串
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const isJSON = (str: string) => {
    if (typeof str == 'string') {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    }
};
export const getFileExtension = (filename: string) => {
    // 使用正则表达式获取文件的后缀名
    const match = filename.match(/\.([^.]+)$/);
    return match ? match[1] : '';
};
export function generateUUID() {
    return '10000000-1000-4000-8000-100000000000'
        .replace(/[018]/g, function (c) {
            return (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16);
        })
        .replace(/-/g, '');
}
